import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Stack,
  Tooltip,
  Typography,
  Button,
  IconButton,
  Popover,
} from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import {
  FaBullseye,
  FaComments,
  FaGraduationCap,
  FaHandshake,
  FaHeart,
  FaStar,
  FaTools,
  FaUserGraduate,
  FaInfoCircle,
  FaQuestionCircle,
} from "react-icons/fa";
import * as Yup from "yup";
import api from "../../../api/axiosInstance";
import { Button as Btn } from "../../../components/atoms/Button";
import { useSnackbar } from "../../../context/SnackbarContext";
import theme from "../../../theme";
import type { ColorsCodeType } from "../../../typings";
import { MentorFeedback } from "../../../typings/scholarship-applications";
import { MentorGuidelinesModal } from "./mentor-guidelines-modal";

interface MentorFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  applicationId: number;
  onRefresh: () => void;
}

const validationSchema = Yup.object({
  future_goals_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  career_awareness_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  social_contribution_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  resilience_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  skills_development_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  future_education_readiness_rating: Yup.number()
    .required("Required")
    .min(1, "Please select a rating"),
  comments_related_to_future_goals: Yup.string().required(
    "Please provide comments on future goals"
  ),
  comments_related_to_social_contribution: Yup.string().required(
    "Please provide comments on social contribution"
  ),
  comments_related_to_resilience: Yup.string().required(
    "Please provide comments on resilience"
  ),
  comments_related_to_skills_development: Yup.string().required(
    "Please provide comments on skills development"
  ),
  comments_related_to_future_education_readiness: Yup.string().required(
    "Please provide comments on future education readiness"
  ),
  final_remarks: Yup.string().required("Please provide final remarks"),
});

// Rating scale definitions based on the provided criteria
const ratingScaleDefinitions = {
  future_goals_rating: [
    {
      value: 1,
      label: "Best Align",
      description: "Goals perfectly align with the objectives of Holding Hand Trust, demonstrating clear synergy and commitment.",
    },
    {
      value: 2,
      label: "High Align",
      description: "Goals mostly align, with minor gaps that can be addressed with guidance or adjustments.",
    },
    {
      value: 3,
      label: "Moderate Align",
      description: "Goals partially align, requiring significant effort to align fully with the objectives.",
    },
    {
      value: 4,
      label: "Below Average Align",
      description: "Goals have minimal alignment, indicating a weak connection to the objectives of the Trust.",
    },
    {
      value: 5,
      label: "Low Align",
      description: "Goals do not align at all, showing a complete mismatch with the objectives of Holding Hand Trust.",
    },
  ],
  career_awareness_rating: [
    {
      value: 1,
      label: "Excellent Understanding",
      description: "Demonstrates a clear understanding of career paths, with goals perfectly aligned to skills and interests; highly informed.",
    },
    {
      value: 2,
      label: "Strong Understanding",
      description: "Shows a good understanding of career paths, with goals largely aligned to skills and interests; ready to make informed decisions.",
    },
    {
      value: 3,
      label: "Moderate Understanding",
      description: "Displays a basic understanding of career paths; some alignment between goals, skills, and interests, but needs more clarity.",
    },
    {
      value: 4,
      label: "Limited Understanding",
      description: "Shows limited understanding of career paths; weak alignment of goals with skills and interests; requires significant guidance.",
    },
    {
      value: 5,
      label: "Poor Understanding",
      description: "Lacks understanding of career paths; goals do not align with skills or interests; not ready to make informed decisions.",
    },
  ],
  social_contribution_rating: [
    {
      value: 1,
      label: "Highly Engaged",
      description: "Demonstrates exceptional awareness and active participation in community service or social projects; highly committed to social growth.",
    },
    {
      value: 2,
      label: "Strongly Engaged",
      description: "Shows good awareness and consistent involvement in community service or social projects; willing to contribute meaningfully.",
    },
    {
      value: 3,
      label: "Moderately Engaged",
      description: "Displays moderate awareness and occasional involvement in community service or social projects; has potential to contribute more.",
    },
    {
      value: 4,
      label: "Limited Engagement",
      description: "Shows limited awareness or minimal involvement in community service or social projects; needs encouragement to engage further.",
    },
    {
      value: 5,
      label: "Not Engaged",
      description: "Lacks awareness or interest in community service or social projects; unwilling or unprepared to contribute to societal growth.",
    },
  ],
  resilience_rating: [
    {
      value: 1,
      label: "Exceptionally Resilient",
      description: "Demonstrates outstanding ability to overcome personal and financial challenges with resilience, adaptability, and determination.",
    },
    {
      value: 2,
      label: "Highly Resilient",
      description: "Shows strong resilience and adaptability in handling personal and financial challenges, with minimal need for external support.",
    },
    {
      value: 3,
      label: "Moderately Resilient",
      description: "Displays moderate resilience and adaptability; capable of overcoming challenges but requires occasional support or guidance.",
    },
    {
      value: 4,
      label: "Limited Resilience",
      description: "Shows limited resilience and adaptability; struggles to manage personal or financial challenges without significant assistance.",
    },
    {
      value: 5,
      label: "Lacks Resilience",
      description: "Unable to effectively cope with personal and financial challenges; lacks adaptability and requires substantial support.",
    },
  ],
  skills_development_rating: [
    {
      value: 1,
      label: "Exceptional Progress",
      description: "Demonstrates outstanding progress in acquiring soft skills, technical skills, or certifications as guided during mentorship.",
    },
    {
      value: 2,
      label: "Significant Progress",
      description: "Shows strong and consistent effort in skill development, with noticeable improvements in soft skills, technical skills, or certifications.",
    },
    {
      value: 3,
      label: "Moderate Progress",
      description: "Displays moderate progress in skill development; some effort made, but room for further improvement.",
    },
    {
      value: 4,
      label: "Limited Progress",
      description: "Shows minimal progress in acquiring soft or technical skills; needs greater commitment to mentorship guidance.",
    },
    {
      value: 5,
      label: "No Progress",
      description: "Lacks any measurable progress in skill development or certification acquisition despite mentorship guidance.",
    },
  ],
  future_education_readiness_rating: [
    {
      value: 1,
      label: "Exceptionally Prepared",
      description: "Demonstrates outstanding awareness and readiness for further education opportunities, with well-researched plans and applications underway.",
    },
    {
      value: 2,
      label: "Highly Prepared",
      description: "Shows strong awareness and preparation for further education, including active engagement in university or scholarship applications.",
    },
    {
      value: 3,
      label: "Moderately Prepared",
      description: "Displays moderate awareness and preparation; has started exploring education opportunities but needs more guidance and effort.",
    },
    {
      value: 4,
      label: "Limited Preparedness",
      description: "Shows minimal awareness or preparation for further education opportunities; requires significant support to take the next steps.",
    },
    {
      value: 5,
      label: "Unprepared",
      description: "Lacks awareness or preparation for further education opportunities and has not taken any meaningful steps toward university or scholarships.",
    },
  ],
};

export default function MentorFeedbackModal({
  open,
  onClose,
  applicationId,
  onRefresh,
}: MentorFeedbackModalProps) {
  const { showSnackbar } = useSnackbar();
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [guidelinesOpen, setGuidelinesOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeRatingField, setActiveRatingField] = useState<string | null>(null);

  const initialValues: MentorFeedback = {
    scholarship_application_id: applicationId,
    future_goals_rating: 0,
    comments_related_to_future_goals: "",
    career_awareness_rating: 0,
    social_contribution_rating: 0,
    comments_related_to_social_contribution: "",
    resilience_rating: 0,
    comments_related_to_resilience: "",
    skills_development_rating: 0,
    comments_related_to_skills_development: "",
    future_education_readiness_rating: 0,
    comments_related_to_future_education_readiness: "",
    final_remarks: "",
  };

  const handleSubmit = async (
    values: MentorFeedback,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      setSubmitStatus("loading");
      console.log("Mentor feedback values:", values);
      
      const response = await api.post(
        "/scholarship-applications/mentor-feedback/",
        values
      );

      console.log("Mentor feedback response:", response.data.data);

      showSnackbar("Mentor feedback submitted successfully!", "success");
      resetForm();
      
      // Refresh the applications list
      onRefresh();

      setTimeout(() => {
        onClose();
        setSubmitStatus("idle");
      }, 1500);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred";
      showSnackbar(errorMessage, "error");
      setSubmitStatus("error");

      setTimeout(() => {
        setSubmitStatus("idle");
        setErrorMessage("");
      }, 3000);
    }
  };



  const handleOpenRatingInfo = (
    event: React.MouseEvent<HTMLElement>,
    fieldName: string
  ) => {
    setAnchorEl(event.currentTarget);
    setActiveRatingField(fieldName);
  };

  const handleCloseRatingInfo = () => {
    setAnchorEl(null);
    setActiveRatingField(null);
  };

  const openRatingInfo = Boolean(anchorEl);

  const RatingField = ({
    name,
    label,
    icon,
  }: {
    name: string;
    label: string;
    icon?: React.ReactNode;
  }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {icon && (
            <Box component="span" sx={{ mr: 1 }}>
              {icon}
            </Box>
          )}
          {label}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => handleOpenRatingInfo(e, name)}
          sx={{
            ml: 1,
            color: "#1976d2",
            "&:hover": {
              backgroundColor: "rgba(99, 102, 241, 0.1)",
            },
          }}
        >
          <FaQuestionCircle size={16} />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Field name={name}>
          {({ field, form }: any) => (
            <Box sx={{ display: "flex" }}>
              {[1, 2, 3, 4, 5].map((star) => {
                const definition =
                  ratingScaleDefinitions[
                    name as keyof typeof ratingScaleDefinitions
                  ]?.[star - 1];
                return (
                  <Tooltip
                    key={star}
                    title={
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
                          {definition?.label || `Rating ${star}`}
                        </Typography>
                        <Typography sx={{ fontSize: "12px" }}>
                          {definition?.description}
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      onClick={() => form.setFieldValue(field.name, star)}
                      sx={{
                        cursor: "pointer",
                        padding: "2px",
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      }}
                    >
                      <FaStar
                        size={24}
                        style={{
                          color: field.value >= star ? "#1976d2" : "#d1d5db",
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </Field>
      </Box>
      <ErrorMessage name={name}>
        {(msg) => (
          <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
            {msg}
          </Typography>
        )}
      </ErrorMessage>
    </Box>
  );

  const TextFieldComponent = ({
    name,
    label,
    placeholder,
    icon,
    multiline = true,
    rows = 3,
  }: {
    name: string;
    label: string;
    placeholder: string;
    icon?: React.ReactNode;
    multiline?: boolean;
    rows?: number;
  }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        component="label"
        htmlFor={name}
        sx={{
          display: "flex",
          alignItems: "center",
          fontSize: "14px",
          fontWeight: 500,
          mb: 1,
        }}
      >
        {icon && (
          <Box component="span" sx={{ mr: 1 }}>
            {icon}
          </Box>
        )}
        {label}
      </Typography>
      <Field name={name}>
        {({ field, meta }: any) => (
          <>
            <MuiTextField
              id={name}
              {...field}
              placeholder={placeholder}
              multiline={multiline}
              rows={rows}
              fullWidth
              variant="outlined"
              error={meta.touched && meta.error ? true : false}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: "14px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                  },
                },
              }}
            />
            {meta.touched && meta.error && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mt: 0.5, display: "block" }}
              >
                {meta.error}
              </Typography>
            )}
          </>
        )}
      </Field>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            maxWidth: "650px",
            width: "100%",
            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: "24px 24px 0",
            fontSize: "24px",
            fontWeight: 600,
            color: "#1e293b",
          }}
        >
          Mentor Feedback
        </DialogTitle>
        <Button
          startIcon={<FaInfoCircle />}
          onClick={() => setGuidelinesOpen(true)}
          sx={{
            textTransform: "none",
            margin: "0 24px",
            backgroundColor: "#f0f7ff",
            color: "#1976d2",
            borderRadius: "20px",
            padding: "8px 16px",
            fontWeight: 500,
            border: "1px solid #e3f2fd",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            "&:hover": {
              backgroundColor: "#e3f2fd",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            },
            transition: "all 0.3s ease",
          }}
        >
          View Mentor Guidelines
        </Button>
        
        <DialogContent sx={{ padding: "16px 24px" }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    mb: 2,
                    color: "#1e293b",
                    fontWeight: 600,
                    fontSize: "16px",
                    borderBottom: "2px solid #ede9fe",
                    paddingBottom: "8px",
                  }}
                >
                  Ratings (1-5 scale)
                </Typography>

                <RatingField
                  name="future_goals_rating"
                  label="Clarity of Aspirations and Future Goals"
                  icon={<FaBullseye />}
                />

                <RatingField
                  name="career_awareness_rating"
                  label="Career Awareness and Planning"
                  icon={<FaUserGraduate />}
                />

                <RatingField
                  name="social_contribution_rating"
                  label="Social Contribution"
                  icon={<FaHandshake />}
                />

                <RatingField
                  name="resilience_rating"
                  label="Personal Growth and Resilience"
                  icon={<FaHeart />}
                />

                <RatingField
                  name="skills_development_rating"
                  label="Development of Skills"
                  icon={<FaTools />}
                />

                <RatingField
                  name="future_education_readiness_rating"
                  label="Readiness for Future Education"
                  icon={<FaGraduationCap />}
                />

                <Typography
                  variant="h6"
                  sx={{
                    mt: 3,
                    mb: 2,
                    color: "#1e293b",
                    fontWeight: 600,
                    fontSize: "16px",
                    borderBottom: "2px solid #ede9fe",
                    paddingBottom: "8px",
                  }}
                >
                  Comments
                </Typography>

                <TextFieldComponent
                  name="comments_related_to_future_goals"
                  label="Future Goals Comments"
                  placeholder="Comments on the student's future goals and vision..."
                  icon={<FaBullseye />}
                  rows={3}
                />

                <TextFieldComponent
                  name="comments_related_to_social_contribution"
                  label="Social Contribution Comments"
                  placeholder="Comments on the student's social contribution and community involvement..."
                  icon={<FaHandshake />}
                  rows={3}
                />

                <TextFieldComponent
                  name="comments_related_to_resilience"
                  label="Resilience Comments"
                  placeholder="Comments on the student's resilience and coping strategies..."
                  icon={<FaHeart />}
                  rows={3}
                />

                <TextFieldComponent
                  name="comments_related_to_skills_development"
                  label="Skills Development Comments"
                  placeholder="Comments on the student's skills development and learning approach..."
                  icon={<FaTools />}
                  rows={3}
                />

                <TextFieldComponent
                  name="comments_related_to_future_education_readiness"
                  label="Future Education Readiness Comments"
                  placeholder="Comments on the student's readiness for future education..."
                  icon={<FaGraduationCap />}
                  rows={3}
                />

                <TextFieldComponent
                  name="final_remarks"
                  label="Final Remarks"
                  placeholder="Overall assessment and final remarks about the student..."
                  icon={<FaComments />}
                  rows={3}
                />

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <Btn
                    buttonColor={
                      theme.palette.colors["black"]["600"] as ColorsCodeType
                    }
                    textColor={
                      theme.palette.colors["white"]["400"] as ColorsCodeType
                    }
                    onClick={onClose}
                  >
                    Cancel
                  </Btn>

                  <Btn
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !isValid ||
                      !dirty ||
                      submitStatus === "loading"
                    }
                  >
                    {submitStatus === "loading"
                      ? "Submitting..."
                      : "Submit Feedback"}
                  </Btn>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Rating Scale Definitions Popover */}
      <Popover
        open={openRatingInfo}
        anchorEl={anchorEl}
        onClose={handleCloseRatingInfo}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 320,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: "10px",
            mt: 1,
          },
        }}
      >
        {activeRatingField && (
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1, color: "#1e293b" }}>
              Rating Scale Definitions
            </Typography>
            {ratingScaleDefinitions[
              activeRatingField as keyof typeof ratingScaleDefinitions
            ]?.map((item) => (
              <Box key={item.value} sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#fff",
                      backgroundColor:
                        item.value === 1
                          ? "#22c55e"
                          : item.value === 2
                          ? "#84cc16"
                          : item.value === 3
                          ? "#eab308"
                          : item.value === 4
                          ? "#f97316"
                          : "#ef4444",
                      borderRadius: "50%",
                      mr: 1,
                    }}
                  >
                    {item.value}
                  </Box>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: "14px", color: "#334155" }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "12px", color: "#64748b", ml: 4 }}>
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>


      
      <MentorGuidelinesModal
        open={guidelinesOpen}
        onClose={() => setGuidelinesOpen(false)}
      />
    </>
  );
} 